import { Typography } from '@mui/material'
import { VendorTrackerComponents } from '../../components/VendorTracker'
import styles from './vendorsTrackerPage.module.scss'
import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState, useRef } from 'react'
import CommonModal from '../../components/atoms/modal/CommonModal'
import SingleSelectSearch from '../../components/atoms/select/SingleSelectSearch'
import {
	addVendorToKazoo,
	getKazooVendors,
	getVendors,
	inviteVendorsByEmail,
} from '../../features/vendorSlice.js'
import { getKazooMembers, getKazooDate } from '../../features/kazooEventSlice.js'
import { RemoveKazooVendorPopup } from '../../components/popups/RemoveKazooVendorPopup.jsx'
import { GeneralPaymentPopup } from '../../components/popups/GeneralPaymentPopup.jsx'
import { getKazooProposals } from '../../features/proposalSlice.js'
import { getKazooFavoriteVendors } from '../../features/kazooEventSlice.js'
import Stepper from '../../components/atoms/Stepper/Stepper.jsx'
import AddVendor from '../../components/VendorTracker/AddVendor.jsx'
import AddVendorDetails from '../../components/VendorTracker/AddVendorDetails.jsx'
import AddVendor2 from '../../components/VendorTracker/AddVendor2.jsx'
import { toastHelper } from '../../util/toastHelper.js'
import { useTutorial } from '../../context/TutorialContext'
import { useTutorialStatus } from '../../hooks/useTutorialStatus'
import { EMAIL_RE } from '../../util/constants.js'
import { useCoupleHasPremium } from '../../hooks/index.js'
import { RemoveProposalPopup } from '../../components/popups/RemoveProposalPopup.jsx'
import PackageSelection from '../vendors-list/Modals/PackageSelection.jsx'
import { getPackages } from '../../features/packageSlice.js'

export default function VendorsTrackerPage() {
	const dispatch = useDispatch()
	const { kazooId } = useParams()

	const vendorSearchList = useSelector((state) => state.vendor.vendorSearchList)

	const vendorSearchListLoading = useSelector(
		(state) => state.vendor.vendorSearchListLoading
	)

	const [selectedVendor, setSelectedVendor] = useState(null)
	const [selectedPackage, setSelectedPackage] = useState(null)
	const [removingVendor, setRemovingVendor] = useState(false)
	const [modal, setModal] = useState('')
	const [activeStep, setActiveStep] = useState(0)
	const [showPaymentPopup, setShowPaymentPopup] = useState(false)

	const rawKazooDate = useSelector((state) => state.kazooEvent.kazooDate)
	const kazooVendors = useSelector(
		(state) => state.vendor.vendors || []
	)

	const kazooDateString = useMemo(() => {
		if (!rawKazooDate) return ''
		const d = new Date(rawKazooDate)
		if (isNaN(d.valueOf())) return ''
		const day = d.getDate()
		const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
		const month = months[d.getMonth()]
		const year = d.getFullYear()
		return `${day} ${month} ${year}`
	}, [rawKazooDate])

	const MAX_FREE_VENDORS = 3
	const isAtVendorLimit = kazooVendors.length >= MAX_FREE_VENDORS

	// Tutorial hooks
	const { hasSeenTutorial, isLoading } = useTutorialStatus('vendor-tracker')
	const { startTutorial, isTutorialActive } = useTutorial()
	// Tutorial effect
	useEffect(() => {
		if (!isLoading && !hasSeenTutorial && !isTutorialActive()) {
			const timer = setTimeout(() => {
				startTutorial('vendor-tracker')
			}, 1000)
			return () => clearTimeout(timer)
		}
	}, [isLoading, hasSeenTutorial, isTutorialActive, startTutorial])

	const openModal = (modal) => setModal(modal)
	const closeModal = () => {
		setActiveStep(0)
		setSelectedVendor(null)
		setModal('')
	}

	const options = useMemo(
		() =>
			vendorSearchList?.length > 0
				? vendorSearchList.map((v) => ({
						...v,
						name: v.businessName,
						businessAddress: '',
					}))
				: [],
		[vendorSearchList]
	)

	const toggleSetRemovingVendor = (vendor) => {
		if (!removingVendor) setSelectedVendor(vendor)
		setRemovingVendor(!removingVendor)
	}

	useEffect(() => {
		if (kazooId) {
			dispatch(getKazooProposals(kazooId))
			dispatch(getKazooFavoriteVendors(kazooId))
			dispatch(getKazooVendors(kazooId))
			dispatch(getKazooMembers(kazooId))
			dispatch(getKazooDate(kazooId))
		}
	}, [dispatch, kazooId])

	const previousVendorIdRef = useRef(null)

	useEffect(() => {
		if (selectedVendor?._id && selectedVendor._id !== previousVendorIdRef.current) {
			dispatch(getPackages({ vendorId: selectedVendor._id }))
			setSelectedPackage(null)
			previousVendorIdRef.current = selectedVendor._id
		}
	}, [dispatch, selectedVendor?._id])

	const kazooMembers = useSelector((s) => s.kazooEvent.kazooMembers) || {}
    const brideTier = kazooMembers?.bride?.payment
    const groomTier = kazooMembers?.groom?.payment
        
    const hasPremiumAccess = useCoupleHasPremium(brideTier, groomTier)

	const onSearch = (searchQuery) => {
		if (!searchQuery) return
		dispatch(
			getVendors({
				search: searchQuery,
				returnFields:
					'email,phone,upperPriceRange,lowerPriceRange',
			})
		)
	}

	const onSelect = (option) => {
		if (!option) return
		setSelectedVendor(option)
	}

	const handleBack = () => {
		setActiveStep((prevStep) => (prevStep <= 0 ? prevStep : prevStep - 1))
	}

	const handleNext = () => {
		if (activeStep === 0) {
            if(!selectedVendor?.email || !EMAIL_RE.test(selectedVendor?.email)){
                return toastHelper('Please enter a valid email', 'error')
            }
		}
        else if(activeStep === 1){
            if (!selectedVendor?.quote || selectedVendor?.quote < 1) {
                return toastHelper('Quote is required and must be greater than 0', 'error')
            }
            if (selectedVendor?.deposit && selectedVendor?.deposit < 1) {
                return toastHelper('Deposit must be greater than 0', 'error')
            }
            if (!selectedVendor?.paymentDueDate) {
                return toastHelper('Please enter a due date', 'error')
            }
            if (selectedVendor?.deposit > selectedVendor?.quote){
                return toastHelper('Deposit cannot be greater than Quote amount', 'error')
            }
            if (!selectedVendor?.status){
                return toastHelper('Payment status is required', 'error')
            }

            const due = selectedVendor?.paymentDueDate
    
            if (!due) {
                return toastHelper('Please select a payment due date', 'error')
            }
    
            const dueDate =
                typeof due?.toDate === 'function' ? due.toDate() : new Date(due)
    
            if (isNaN(dueDate.getTime()) || dueDate <= new Date()) {
                return toastHelper('Payment due date must be in the future', 'error')
            }
        }

		setActiveStep((prevStep) => (prevStep >= 3 ? prevStep : prevStep + 1))
	}

	const handleAddVendor = async () => {
		try {
			if (
				!selectedVendor?.email ||
				!EMAIL_RE.test(selectedVendor?.email)
			) {
				return toastHelper('Please enter a valid email', 'error')
			}
			if (!selectedVendor?.quote || selectedVendor?.quote < 1) {
				return toastHelper(
					'Quote is required and must be greater than 0',
					'error'
				)
			}
			/**
			 * TODO:: isDepositPaid is true then implement payment gateway functionality using stripe
			*/
			if (selectedVendor?.isDepositPaid) {
				if (!selectedVendor?.deposit || selectedVendor?.deposit < 1) {
					return toastHelper(
						'Deposit is required and must be greater than 0',
						'error'
					)
				}

				if (selectedVendor?.deposit > selectedVendor?.quote) {
					return toastHelper(
						'Deposit cannot be greater than Quote amount',
						'error'
					)
				}
			}
			const due = selectedVendor?.paymentDueDate
			if (!due) {
				return toastHelper('Please enter a due date', 'error')
			}
			const dueDate =
				typeof due?.toDate === 'function' ? due.toDate() : new Date(due)
			if (isNaN(dueDate.getTime()) || dueDate <= new Date()) {
				return toastHelper(
					'Payment due date must be in the future',
					'error'
				)
			}

			const vendorPayload = {
				...selectedVendor,
				paymentDueDate: dueDate.toISOString(),
			}

			if (selectedVendor?._id) {
				await dispatch(
					addVendorToKazoo({
						kazooId,
						vendorId: selectedVendor._id,
						vendorData: vendorPayload,
						...(selectedPackage?._id ? { packageId: selectedPackage?._id } : {}),
						...(selectedPackage?.selectedAddOns ? { addOnIds: selectedPackage?.selectedAddOns } : {}),
					})
				).unwrap()
			} else if (selectedVendor?.email) {
				await dispatch(
					inviteVendorsByEmail({
						kazooId,
						emails: [selectedVendor.email],
						vendorData: vendorPayload,
						...(selectedPackage?._id ? { packageId: selectedPackage?._id } : {}),
						...(selectedPackage?.selectedAddOns ? { addOnIds: selectedPackage?.selectedAddOns } : {}),
					})
				).unwrap()
			} else {
				return toastHelper(
					'Please enter a valid email or select a vendor from the list',
					'error'
				)
			}

			dispatch(getKazooProposals(kazooId))
		} catch (error) {
			console.error('Failed to add vendor:', error)
		}
		closeModal()
	}

	return (
		<div className={styles.pageWrap}>
			<div className={styles.pageHeader}>
				<Typography component="h1">Vendor Tracker</Typography>
				<div className={styles.right}>
					{kazooDateString && (
						<div className={styles.date}>
							<img src="/images/calendar_line.svg" alt="calendar" />
							<span>{kazooDateString}</span>
						</div>
					)}
					<ButtonCustom
						variant="default"
						onClick={() => {
							if (isAtVendorLimit && !hasPremiumAccess) {
								setShowPaymentPopup(true)
							} else {
								openModal('invite')
							}
						}}
						data-cy="add-vendors"
					>
						+ Add Vendor
					</ButtonCustom>
				</div>
			</div>
			<VendorTrackerComponents onRemove={toggleSetRemovingVendor} />
			<CommonModal
				open={modal === 'invite'}
				handleClose={closeModal}
				heading="Add Vendor"
				className={styles.inviteModal}
				footerContent={
					<div className={styles.innerModal}>
						<div className="">
							<Stepper
								totalSteps={4}
								subSteps={3}
								activeStep={activeStep + 1}
							/>
						</div>
						<div className={styles.innerModalButtonGroup}>
							{activeStep > 0 && (
								<ButtonCustom
									variant="link"
									size="lg"
									onClick={handleBack}
								>
									Previous
								</ButtonCustom>
							)}
							{activeStep === 3 ? (
								<ButtonCustom
									variant="default"
									size="lg"
									onClick={handleAddVendor}
									disabled={!selectedVendor?.checked}
								>
									Send Invite
								</ButtonCustom>
							) : (
								<ButtonCustom
									variant="default"
									size="lg"
									onClick={handleNext}
								>
									Next
								</ButtonCustom>
							)}
						</div>
					</div>
				}
			>
				<>
					{/* TODO:: Uncomment if we also want multi select emails
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginBottom: '1rem',
						}}
					>
						<ButtonCustom
							type="button"
							onClick={() => openModal('email')}
						>
							Invite by email
						</ButtonCustom>
					</div> */}
					{activeStep === 0 && (
						<>
							<SingleSelectSearch
								label="Search Vendors"
								searchPlaceholder="Search Vendors..."
								options={options}
								onSelect={onSelect}
								onSearch={onSearch}
								isLoading={vendorSearchListLoading}
							/>
							<br />
							<AddVendor
								vendor={selectedVendor}
								setVendor={setSelectedVendor}
							/>
						</>
					)}

					{/** TODO:: Attach to Wedding form
					 * Assign vendor to event happening (ceremony, reception, etc.).
					 * Link to timeline (task block auto-generated if category matches e.g., Photographer → photo shoot tasks).
					 * NOTE: Vendor tasks API a later feature.
					 * If venue → link to Venue object.
					 */}

					{activeStep === 1 && (
						<AddVendor2
							vendor={selectedVendor}
							setVendor={setSelectedVendor}
							setSelectedPackage={setSelectedPackage}
						/>
					)}

					{activeStep === 2 && (
						<PackageSelection
							selectedPackage={selectedPackage}
							setSelectedPackage={setSelectedPackage}
						/>
					)}

					{activeStep === 3 && (
						<AddVendorDetails
							vendor={selectedVendor}
							setVendor={setSelectedVendor}
							selectedPackage={selectedPackage}
						/>
					)}
				</>
			</CommonModal>

			{removingVendor && (
				<RemoveKazooVendorPopup
					kazooId={kazooId}
					vendor={selectedVendor}
					clickHandler={toggleSetRemovingVendor}
					removingVendor={removingVendor}
				/>
			)}
			{showPaymentPopup && (
				<GeneralPaymentPopup
					clickHandler={() => setShowPaymentPopup(false)}
				/>
			)}

			{kazooId && <RemoveProposalPopup kazooId={kazooId} />}
		</div>
	)
}
